//client/src/components/plataforma/politicasETermos/PolicyFormPage.j
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePolicies } from '../../../hooks/usePolicies';
import useAlert from '../../../hooks/useAlert';
import { Save, Loader2, Gavel } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import GerarTextoIABox from '../../ai/GerarTextoIABox';

const PolicyFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const { getPolicy, createPolicy, updatePolicy, loading } = usePolicies();
    const { setAlert } = useAlert();

    const [formData, setFormData] = useState({
        title: '',
        type: 'terms-of-use',
        audience: 'platform_user',
        status: 'draft',
        content: ''
    });

    const fetchPolicyData = useCallback(async () => {
        if (isEditing) {
            try {
                const result = await getPolicy(id);
                setFormData(result.data);
            } catch (err) {
                setAlert(`Erro ao carregar documento: ${err.message}`, 'error');
            }
        }
    }, [id, isEditing, getPolicy, setAlert]);

    useEffect(() => {
        fetchPolicyData();
    }, [fetchPolicyData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await updatePolicy(id, formData);
            } else {
                await createPolicy(formData);
            }
            setAlert(`Documento "${formData.title}" salvo com sucesso!`, 'success');
            navigate('/site-institucional/politicas');
        } catch (err) {
            setAlert(`Erro ao salvar: ${err.message}`, 'error');
        }
    };

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title={isEditing ? 'Editar Documento' : 'Novo Documento'}
                    icon={<Gavel className="icon-title" />}
                    navBack={{ to: '/site-institucional/politicas', text: 'Voltar' }}
                />

                <div className="content-panel mt-6">
                    <GerarTextoIABox
                        endpoint="/admin/policies/gerar-ia"
                        extraPayload={{ type: formData.type, audience: formData.audience }}
                        placeholder='Descreva o contexto do negócio — ex: "plataforma de agendamento, coletamos nome/email/telefone dos clientes, cancelamento com até 24h de antecedência"'
                        successMessage="Documento gerado! Revise com cuidado (e idealmente com jurídico) antes de publicar."
                        onGenerated={(conteudoGerado) => setFormData(prev => ({ ...prev, content: conteudoGerado }))}
                    />
                    <div className="form-grid-container grid-cols-1 md:grid-cols-3">
                        <div className="form-group md:col-span-3">
                            <label htmlFor="title">Título do Documento*</label>
                            <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} className="form-input" required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="type">Tipo de Documento*</label>
                            {/* ✅ AJUSTE: Textos das opções traduzidos */}
                            <select id="type" name="type" value={formData.type} onChange={handleChange} className="form-input" disabled={isEditing}>
                                <option value="terms-of-use">Termos de Uso</option>
                                <option value="privacy-policy">Política de Privacidade</option>
                                <option value="lgpd">LGPD</option>
                                <option value="cancellation-policy">Política de Cancelamento</option>
                            </select>
                             {isEditing && <p className="form-hint">O tipo não pode ser alterado após a criação.</p>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="audience">Público-Alvo*</label>
                            {/* ✅ AJUSTE: Textos das opções traduzidos */}
                            <select id="audience" name="audience" value={formData.audience} onChange={handleChange} className="form-input">
                                <option value="platform_user">Usuários da Plataforma</option>
                                <option value="end_customer">Clientes Finais</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="status">Status</label>
                            <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-input">
                                <option value="draft">Rascunho</option>
                                <option value="published">Publicado</option>
                            </select>
                        </div>
                         <div className="form-group md:col-span-3">
                            <label htmlFor="content">Conteúdo (aceita HTML)</label>
                            <textarea id="content" name="content" value={formData.content} onChange={handleChange} className="form-input" rows="20" placeholder="<h2>1. Título</h2><p>Parágrafo de texto...</p>"></textarea>
                        </div>
                    </div>
                </div>

                <FabSaveButton loading={loading} label="Salvar" />
            </form>
        </div>
    );
};

export default PolicyFormPage;